import React, { useEffect, useRef, useState } from 'react';
import { syncBillingStatus } from '../lib/billing';
import { supabase } from '../supabase';

// ─── BillingSuccess ─────────────────────────────────────────────────────────
//
// Landing target after a successful Stripe Checkout. URL shape:
//
//   https://tryposted.app/?view=billing-success&session_id=cs_test_xyz
//
// Race condition we handle here: the operator gets here ~1s after Stripe
// completes checkout, but the customer.subscription.created webhook may
// take 1-3s to fire and persist the trialing status. Polling
// v_company_billing_state every 2s for up to 30s catches the typical
// case; the manual "Refresh" button calls /api/stripe/sync-status which
// pulls directly from Stripe (bypasses webhook delay entirely) for the
// rare longer cases.
//
// Once status flips to 'trialing' or 'active', show the success copy and
// a continue button. App.js's view-routing clears the ?view= URL param
// when the operator clicks Continue.

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 30000;

export default function BillingSuccess({ companyId, onContinue }) {
  const [billing, setBilling] = useState(null);
  const [polling, setPolling] = useState(true);
  const [error, setError] = useState('');
  const [syncing, setSyncing] = useState(false);
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    if (!companyId) return undefined;

    let interval = null;
    const start = Date.now();

    async function pollOnce() {
      const { data, error: e } = await supabase
        .from('v_company_billing_state')
        .select('*')
        .eq('company_id', companyId)
        .maybeSingle();
      if (cancelledRef.current) return;
      if (e) {
        setError('Couldn\'t check subscription status. Try refreshing.');
        if (interval) clearInterval(interval);
        setPolling(false);
        return;
      }
      setBilling(data || null);
      if (data && (data.subscription_status === 'trialing' || data.subscription_status === 'active')) {
        if (interval) clearInterval(interval);
        setPolling(false);
        return;
      }
      if (Date.now() - start > POLL_TIMEOUT_MS) {
        if (interval) clearInterval(interval);
        setPolling(false);
      }
    }

    pollOnce();
    interval = setInterval(pollOnce, POLL_INTERVAL_MS);
    return () => {
      cancelledRef.current = true;
      if (interval) clearInterval(interval);
    };
  }, [companyId]);

  async function manualRefresh() {
    if (syncing) return;
    setSyncing(true);
    setError('');
    const r = await syncBillingStatus();
    if (!r.ok) {
      setError((r.body && r.body.message) || 'Sync failed. Try opening Settings → Billing.');
    } else if (r.body) {
      // After a manual sync, re-pull v_company_billing_state for the
      // canonical view shape.
      const { data } = await supabase
        .from('v_company_billing_state')
        .select('*')
        .eq('company_id', companyId)
        .maybeSingle();
      setBilling(data || null);
      if (data && (data.subscription_status === 'trialing' || data.subscription_status === 'active')) {
        setPolling(false);
      }
    }
    setSyncing(false);
  }

  const isReady = billing && (billing.subscription_status === 'trialing' || billing.subscription_status === 'active');
  const trialEnd = billing && billing.trial_ends_at
    ? new Date(billing.trial_ends_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    : null;

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-6">
      <div className="bg-white rounded-2xl border border-gray-200 p-8 w-full max-w-md text-center">
        {isReady ? (
          <>
            <div style={{ fontSize: 36, marginBottom: 12 }}>🎉</div>
            <div className="text-xl font-semibold text-gray-900 mb-2">Welcome to Posted!</div>
            <div className="text-sm text-gray-600 leading-relaxed mb-6">
              Your trial has started.
              {trialEnd && (
                <> You won't be charged until <span className="font-semibold text-gray-900">{trialEnd}</span>.</>
              )}
            </div>
            <button
              type="button"
              onClick={onContinue}
              className="w-full bg-blue-600 text-white rounded-lg py-2.5 text-sm font-semibold hover:bg-blue-700"
            >
              Continue to dashboard
            </button>
          </>
        ) : polling ? (
          <>
            <div style={{ fontSize: 28, marginBottom: 12 }}>⏳</div>
            <div className="text-lg font-semibold text-gray-900 mb-2">Setting up your subscription</div>
            <div className="text-sm text-gray-500 leading-relaxed mb-4">
              We're processing your subscription with Stripe. This usually takes a few seconds.
            </div>
            <div className="text-xs text-gray-400">Checking…</div>
          </>
        ) : (
          <>
            <div style={{ fontSize: 28, marginBottom: 12 }}>⏱</div>
            <div className="text-lg font-semibold text-gray-900 mb-2">Still processing</div>
            <div className="text-sm text-gray-500 leading-relaxed mb-4">
              Your payment was accepted but Posted hasn't received the confirmation yet. This is rare — usually
              caused by a brief Stripe delay.
            </div>
            {error && (
              <div className="text-xs text-red-500 bg-red-50 rounded-md px-3 py-2 mb-3">{error}</div>
            )}
            <button
              type="button"
              onClick={manualRefresh}
              disabled={syncing}
              className="w-full bg-blue-600 text-white rounded-lg py-2.5 text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 mb-2"
            >
              {syncing ? 'Refreshing…' : 'Refresh status'}
            </button>
            <button
              type="button"
              onClick={onContinue}
              className="w-full text-sm text-gray-500 hover:text-gray-700 hover:underline"
              style={{ background: 'transparent', border: 'none', padding: 8, cursor: 'pointer' }}
            >
              Continue anyway
            </button>
          </>
        )}
      </div>
    </div>
  );
}
